import { useAuthFrontendApis } from '@propelauth/frontend-apis-react';
import { motion } from 'framer-motion';
import { ArrowLeft, Mail } from 'lucide-react';
import { useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';

interface PasswordResetFormProps {
  email: string;
  onBackToLogin?: () => void;
}

type ResetStep = 'reset-form' | 'reset-sent';

export function PasswordResetForm({ email, onBackToLogin }: PasswordResetFormProps) {
  const [step, setStep] = useState<ResetStep>('reset-form');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { sendForgotPasswordEmail } = useAuthFrontendApis();

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const response = await sendForgotPasswordEmail({ email });

      await response.handle({
        success: () => {
          setStep('reset-sent');
        },
        badRequest: error => {
          setError(error.user_facing_error || 'Invalid email address');
        },
        unexpectedOrUnhandled: error => {
          setError('An unexpected error occurred. Please try again.');
        },
      });
    } catch (error) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendReset = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await sendForgotPasswordEmail({ email });

      await response.handle({
        success: () => {
          // Already on reset-sent step, just show success
        },
        badRequest: error => {
          setError(error.user_facing_error || 'Invalid email address');
        },
        unexpectedOrUnhandled: error => {
          setError('An unexpected error occurred. Please try again.');
        },
      });
    } catch (error) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (step === 'reset-sent') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, type: 'spring', stiffness: 300, damping: 30 }}
        className="space-y-6"
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email-sent" className="text-sm font-medium text-neutral-950">
              Email
            </Label>
            <Input
              id="email-sent"
              type="email"
              value={email}
              className="h-11 border-neutral-200 text-neutral-950"
              disabled
            />
          </div>

          <div className="p-4 border border-green-200 rounded-lg bg-green-50">
            <div className="flex items-center space-x-2">
              <Mail className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-sm font-medium text-green-800">Reset link sent!</p>
                <p className="text-sm text-green-700">
                  Check your email for a password reset link. Click the link to reset your password.
                </p>
              </div>
            </div>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Button
            type="button"
            onClick={handleResendReset}
            variant="outline"
            className="w-full h-11 border-neutral-200 hover:bg-neutral-50 text-neutral-600"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Spinner size={16} className="mr-2" />
                Resending...
              </>
            ) : (
              'Resend Reset Link'
            )}
          </Button>
        </div>

        <div className="text-center">
          <button
            type="button"
            onClick={onBackToLogin}
            className="inline-flex items-center text-sm transition-colors text-neutral-600 hover:text-neutral-900"
          >
            <ArrowLeft className="w-3 h-3 mr-1" />
            Back to login
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, type: 'spring', stiffness: 300, damping: 30 }}
      className="space-y-6"
    >
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={handlePasswordReset} className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="email-reset" className="text-sm font-medium text-neutral-950">
            Email
          </Label>
          <Input
            id="email-reset"
            type="email"
            value={email}
            className="h-11 border-neutral-200 text-neutral-950"
            disabled
          />
        </div>

        <div className="space-y-3">
          <Button
            type="submit"
            className="w-full font-medium bg-black h-11 hover:bg-gray-800"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Spinner size={16} className="mr-2" color="white" />
                Sending reset link...
              </>
            ) : (
              'Send Reset Link'
            )}
          </Button>
        </div>
      </form>

      <div className="text-center">
        <button
          type="button"
          onClick={onBackToLogin}
          className="inline-flex items-center text-sm transition-colors text-neutral-600 hover:text-neutral-900"
        >
          <ArrowLeft className="w-3 h-3 mr-1" />
          Back to login
        </button>
      </div>
    </motion.div>
  );
}
